import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../component/pages/Falcon360/dashboard/dashboard.js";
import Header from "../component/header/Header.jsx";
import Sidebar from "../component/sidebar/Sidebar.js";
import Footer from "../component/footer/Footer.js";
import AuditLogsTable from "../component/common/audit/AuditLogs.js";
import ImportData from "../component/pages/Falcon360/upload/ImportData.js";
import ErrorPage from "../component/common/ErrorPage.js";
import DataSource from "../component/pages/Falcon360/dataSource/DataSource.js";
import ChatInterface from "../ai-agent/ui/ChatInterface.js";
import ChatModal from "../ai-agent/ui/ChatModal.js";
import DatabaseConnectionsTable from "../component/pages/Falcon360/dataSource/DataConnections.js";
import UploadedObjectsTable from "../component/pages/Falcon360/upload/UploadedObjectsTable.js";
import { LoaderCircle } from "lucide-react";
import axiosClient from "../api-client/AxiosClient.js";
import FalconAIChatbot from "../ai-agent/ui/FalconAIChatbot.js";
import AIChatInterface from "../ai-agent/ui/aiagent.js";
import HelpPage from "../ai-agent/ui/help.js";
import GenerateScript from "../component/pages/Scriptly/GenerateScript.js";


const Layout = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [isRolesLoaded, setIsRolesLoaded] = useState(false);
  const [isUserGuideLoaded, setIsUserGuideLoaded] = useState(false);
  const [run, setRun] = useState(false);

  const isAgentRoute = location.pathname === "/ai-agent";
  const isViewDataRoute = location.pathname === "/view-data";
  const isHistoryPage = location.pathname.includes("/history-data");
  // Define valid paths for authenticated users
  const validPaths = [
    "/dashboard",
    "/upload",
    "/mapping",
    "/view-data",
    "/ai-agent",
    "/profile",
    "/falcon-ai",
    "/data-sources",
    "/data-sources/add-data-source",
    "/upload/add-upload",
    "/history-data",
    "/code-gen",
    "/scriptly",
    "/admin-interface",
    "/help",
    // Add all other valid authenticated routes here
  ];

  const fetchisUserGuideViewed = async () => {
    const response = await axiosClient.get("api/User/GuideViewStatus");
    setRun(response.data || false);
    setIsUserGuideLoaded(true);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosClient.get("/api/User/Permissions");
        if (response && response.data) {
          const merged = {};

          Object.values(response.data).forEach((role) => {
            Object.entries(role).forEach(([module, permissions]) => {
              if (!merged[module]) {
                merged[module] = { ...permissions };
              } else {
                Object.keys(permissions).forEach((key) => {
                  merged[module][key] = merged[module][key] || permissions[key];
                });
              }
            });
          });
          setTimeout(() => {
            setIsRolesLoaded(true);
          }, 1000);
          sessionStorage.setItem("rolesAndPermission", JSON.stringify(merged));
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    fetchisUserGuideViewed();
  }, []);

  // Check if current path is valid
  const isValidPath =
    validPaths.includes(location.pathname) ||
    location.pathname.startsWith("/history-data/");

  // If path is invalid, redirect to dashboard
  if (!isValidPath && location.pathname !== "/") {
    return <Navigate to="/dashboard" replace />;
  }

  const closeChat = () => setChatOpen(false);
  const toggleMinimize = () => setChatMinimized((prev) => !prev);

  const isScriptlyRoute = location.pathname === "/scriptly";

  // Calculate content width based on route and sidebar state
  const getContentStyle = () => {
    const baseStyle = {
      marginLeft: !isAgentRoute ? (isOpen ? "0px" : "80px") : "0px",
      marginTop: "80px", // Add margin-top to account for fixed header
      padding: (!isAgentRoute && !isScriptlyRoute) ? "10px" : "0px",
      width: "100%",
      transition: "all 0.3s ease-in-out",
      height: isScriptlyRoute ? "calc(100vh - 80px)" : "auto",
      overflow: isScriptlyRoute ? "hidden" : "visible",
    };

    if (isHistoryPage) {
      return {
        ...baseStyle,
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    // Special handling for view-data route (CreateTable component)
    if (isViewDataRoute) {
      return {
        ...baseStyle,
        maxWidth: isOpen ? "calc(100vw)" : "calc(100vw - 50px)",
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    return baseStyle;
  };

  return (
    <>
      {isRolesLoaded && isUserGuideLoaded ? (
        <div
          className="app-wrapper"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setRun={setRun}
            run={run}
          />

          <div
            className={`app-container ${isAgentRoute ? "h-screen" : ""}`}
            style={{ display: "flex", flex: 1 }}
          >
            {!isAgentRoute && <Sidebar isOpen={isOpen} />}

            <div className="content" style={getContentStyle()}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/data-sources"
                  element={<DatabaseConnectionsTable />}
                />
                <Route path="/upload" element={<UploadedObjectsTable />} />
                <Route path="/logs" element={<AuditLogsTable />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/ai-agent" element={<ChatInterface />} />
                <Route path="/falcon-ai" element={<FalconAIChatbot />} />
                <Route path="/code-gen" element={<AIChatInterface />} />

                <Route path="/help" element={<HelpPage />} />
                <Route
                  path="/data-sources/add-data-source"
                  element={<DataSource />}
                />
                <Route path="/upload/add-upload" element={<ImportData />} />
                <Route path="/scriptly" element={<GenerateScript />} />
                {/* Catch all invalid paths and redirect to dashboard */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </div>
            {/* Floating Chat Icon */}
            {/* {!isAgentRoute && (
              <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end">
                <div className="relative bg-blue-900 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md animate-pulse">
                  Ask Me...
                  <div className="absolute bottom-[-8px] right-3 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-blue-800"></div>
                </div>

                <div
                  onClick={openChat}
                  className="mt-2 w-14 h-14 cursor-pointer rounded-full border-2 border-blue-900 bg-white shadow-lg hover:shadow-xl transition-shadow animate-pulse overflow-hidden"
                >
                  <img
                    src={BotIcon}
                    alt="Chat Bot"
                    className="p-2 object-cover w-full h-full"
                  />
                </div>
              </div>
            )} */}

            {/* Chat Modal */}
            <ChatModal
              open={chatOpen}
              minimized={chatMinimized}
              onClose={closeChat}
              onMinimize={toggleMinimize}
            >
              <ChatInterface />
            </ChatModal>
          </div>

          <Footer />
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <LoaderCircle className="animate-spin text-blue-900" size={50} />
        </div>
      )}
    </>
  );
};

export default Layout;
